import { type MutableRefObject, useEffect, useRef } from 'react';

import { isEqual } from 'lodash';

import { glUpdateFacesSubject } from '@pkg/components/Sections/Profile/pubSub/subjects';
import { useGetPortfolioLidTokensData } from '@pkg/hooks/liquid-id/index';
import { randomId } from '@pkg/utils';
import WebGL from '@pkg/webgl';

import { type TokenV2Metadata } from '@app/client';

type useExtraLiquidIDProps = {
    ownerID: string | undefined;
    wrapperRef: MutableRefObject<HTMLDivElement | null>;
    isScrollingElement?: boolean;
    backgroundVisibility?: boolean;
    delayRender?: number;
};

export const useExtraLiquidID = ({
    ownerID,
    wrapperRef,
    isScrollingElement = true,
    backgroundVisibility = true,
    delayRender,
}: useExtraLiquidIDProps) => {
    const lidLayerID = useRef<string>(randomId());
    const layerRef = useRef();
    const currentData = useRef<TokenV2Metadata[]>();
    const { data: liquidIdData } = useGetPortfolioLidTokensData(ownerID);

    useEffect(() => {
        if (!wrapperRef.current) return;
        let timeout: NodeJS.Timeout | null = null;

        if (layerRef.current) {
            WebGL.updateLayer(lidLayerID.current, wrapperRef.current, {
                isScrollingElement: isScrollingElement,
            });
        } else {
            if (delayRender) {
                timeout = setTimeout(() => {
                    addLIDLayer();
                }, delayRender);
            } else {
                addLIDLayer();
            }
        }

        return () => {
            deleteLIDLayer();
            if (timeout) clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wrapperRef, isScrollingElement]);

    const compareData = () => {
        if (!currentData.current) return true;

        if (currentData.current?.length !== liquidIdData?.length) {
            return true;
        }
        const newItems = liquidIdData ? [...liquidIdData] : [];
        newItems.sort();
        currentData.current?.sort();

        return !isEqual(newItems, currentData.current);
    };

    useEffect(() => {
        const layer = WebGL.getLayer(lidLayerID.current);
        if (!layer) return;

        setData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liquidIdData, layerRef]);

    const setData = () => {
        if (!compareData()) return;
        if (liquidIdData) {
            glUpdateFacesSubject.next({ lidID: lidLayerID.current, isCancel: false, items: liquidIdData });
            currentData.current = liquidIdData;
        }
    };

    const addLIDLayer = async () => {
        await WebGL.ready();
        await WebGL.world?.addLiquidIDLayer(lidLayerID.current, wrapperRef.current);
        const layer = WebGL.getLayer(lidLayerID.current);

        WebGL.updateLayer(lidLayerID.current, wrapperRef.current, {
            isScrollingElement: isScrollingElement,
        });
        layer.scene.setBackgroundVisibility(backgroundVisibility);

        layerRef.current = layer;
        layer.animateIn('fade-in');

        setData();
    };

    const deleteLIDLayer = () => {
        WebGL.deleteLayer(lidLayerID.current);
    };
};
